import Vue from 'vue'
import axios from 'axios'
import VueAxios from 'vue-axios'
import router from '@/router'
import AppConfig from '@/constants/app-config'
import JwtService from '@/api/jwt.service'

function isNetworkError(err) {
  return !!err.isAxiosError && !err.response
}

function throwNetworkError(err) {
  throw new Error('Network error', err)
}

const ApiService = {
  init() {
    Vue.use(VueAxios, axios)
    Vue.axios.defaults.baseURL = AppConfig.apiUrl

    axios.interceptors.request.use(
      (config) => {
        return config
      },
      (error) => {
        window.scrollTo(0, 0)
        return Promise.reject(error)
      }
    )
    axios.interceptors.response.use(
      (response) => {
        return response
      },
      (err) => {
        window.scrollTo(0, 0)
        if (isNetworkError(err)) {
          router.push({ name: 'error-network' })
          throwNetworkError(err)
          return Promise.reject(err)
        } else {
          const status = err.response.status
          if (status === '401') {
            router.push({ name: 'logout' })
          } else if (status === '503') {
            router.push({ name: 'error-503' })
          } else if (status === '500') {
            router.push({ name: 'error-500' })
          }
          return Promise.reject(err.response)
        }
      }
    )
  },

  setHeaders() {
    const token = JwtService.getToken()
    if (token) {
      Vue.axios.defaults.headers.common.Authorization = `Bearer ${token}`
    }
  },

  query(resource, params) {
    this.setHeaders()
    return Vue.axios.get(resource, params)
  },

  get(resource, slug = '') {
    this.setHeaders()
    return Vue.axios.get(`${resource}${slug ? '/' + slug : ''}`)
  },

  post(resource, payload, params) {
    this.setHeaders()
    return Vue.axios.post(`${resource}`, payload, params)
  },

  update(resource, payload, params) {
    this.setHeaders()
    return Vue.axios.patch(`${resource}`, payload, params)
  },

  put(resource, params) {
    return Vue.axios.put(`${resource}`, params)
  },

  delete(resource, params) {
    return Vue.axios.delete(resource, { data: params })
  }
}

export default ApiService

/* ----------------------------------------------------------------------
>>> Payment Service
---------------------------------------------------------------------- */

export const PaymentService = {
  generatePaymentReference: (payload) => {
    return ApiService.post('v2/miportal/generate-oxxo-reference', payload)
  },
  generatePaymentCLABE: (payload) => {
    return ApiService.post('v2/miportal/generate-spei-clabe', payload)
  },
  payByCreditCard: (payload) => {
    return ApiService.post('v2/miportal/pay', payload)
  },
  saveCard: (payload) => {
    return ApiService.post('/miportal/save-card', payload)
  },
  getPendingPayment: (rechargeAmount) => {
    const rechargeAmountParam = rechargeAmount
      ? `?recharge_amount=${rechargeAmount}`
      : ''
    return ApiService.get(`/miportal/pending-payment${rechargeAmountParam}`)
  }
}

/* ----------------------------------------------------------------------
>>> Session Service
---------------------------------------------------------------------- */

export const SessionService = {
  login: (credentials) => {
    return ApiService.post('v2/miportal/login', credentials)
  }
}

/* ----------------------------------------------------------------------
>>> Driver Service
---------------------------------------------------------------------- */

export const DriverService = {
  getAvailablePlans: () => {
    return ApiService.get('/miportal/available-plans')
  },
  updatePlan: (payload) => {
    return ApiService.update('/miportal/update-plan', payload)
  }
}
